import React, { useCallback, memo } from 'react';
import { CheckCircle2Icon, ShieldCheckIcon, ZapIcon } from 'lucide-react';
// Memoizar componentes de beneficios
const Benefit = memo(({
  text
}: {
  text: React.ReactNode;
}) => <div className="flex items-start space-x-2 justify-center lg:justify-start" data-id="element-1092">
    <CheckCircle2Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#2B5FD9] flex-shrink-0 mt-0.5" data-id="element-1093" />
    <span className="text-xs sm:text-sm text-[#6B7280] text-left" data-id="element-1094">{text}</span>
  </div>);
Benefit.displayName = 'Benefit';
const Badge = memo(({
  icon: Icon,
  text
}: {
  icon: React.ElementType;
  text: React.ReactNode;
}) => <div className="flex items-center gap-1.5 text-xs sm:text-sm text-[#6B7280]" data-id="element-1095">
      <Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#2B5FD9]" data-id="element-1096" />
      <span data-id="element-1097">{text}</span>
    </div>);
Badge.displayName = 'Badge';
export function Hero() {
  const scrollToSection = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, []);
  return <section id="hero" className="bg-white flex items-center pt-6 md:pt-12 pb-8 md:pb-20" aria-label="Hero Creditual" data-id="element-1098">
      <div className="container mx-auto max-w-7xl px-4 py-4 md:py-12" data-id="element-1099">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center" data-id="element-1100">
          {/* TEXTO */}
          <div className="text-center lg:text-left space-y-3 md:space-y-6" data-id="element-1101">
            <div className="flex items-center justify-center lg:justify-start gap-2 flex-wrap" data-id="element-1102">
              <p className="text-[#2B5FD9] font-medium text-xs md:text-base" data-id="element-1103">
                Te ayudamos con tu crédito hipotecario
              </p>
              <span className="px-3 py-1 bg-green-50 border border-green-200 text-green-700 text-xs md:text-sm font-bold rounded-full" data-id="element-1104">
                100% Gratis
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] leading-tight" data-id="element-1105">
              Aumenta tus posibilidades de tener un hipotecario... y a la mejor
              tasa
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-[#6B7280] leading-relaxed max-w-xl mx-auto lg:mx-0" data-id="element-1106">
              Con un solo formulario, Creditual evalúa tu perfil en múltiples
              instituciones financieras y te muestra en qué lugares es más
              probable que te aprueben y con qué tasa pagarías menos dividendo.
            </p>

            <div className="space-y-1.5 sm:space-y-2.5" aria-label="Beneficios clave" data-id="element-1107">
              <Benefit text={<>
                    <strong data-id="element-1109">Más probabilidad de aprobación:</strong> encontramos
                    las instituciones donde tu perfil encaja mejor.
                  </>} data-id="element-1108" />
              <Benefit text={<>
                    <strong data-id="element-1111">Mejor tasa y dividendo:</strong> comparamos
                    alternativas para que pagues menos por tu crédito.
                  </>} data-id="element-1110" />
              <Benefit text={<>
                    <strong data-id="element-1113">Un solo formulario:</strong> dejamos atrás semanas
                    de trámites y cotizaciones banco por banco.
                  </>} data-id="element-1112" />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 md:gap-3 justify-center lg:justify-start pt-2 md:pt-4" data-id="element-1114">
              <button onClick={() => scrollToSection('calculadora')} className="px-5 md:px-6 py-2.5 md:py-3 bg-[#2B5FD9] text-white text-sm md:text-base font-medium rounded-xl hover:bg-[#1E4BB8] transition-colors" aria-label="Evaluar mi crédito" data-id="element-1115">
                Evaluar ahora →
              </button>
              <button onClick={() => scrollToSection('como-funciona')} className="px-5 md:px-6 py-2.5 md:py-3 bg-white border border-[#2B5FD9] text-[#2B5FD9] text-sm md:text-base font-medium rounded-xl hover:bg-gray-50 transition-colors" aria-label="Ver cómo funciona" data-id="element-1116">
                ¿Cómo funciona?
              </button>
            </div>

            <div className="flex flex-wrap justify-center lg:justify-start items-center gap-2 md:gap-4 pt-1" data-id="element-1117">
              <Badge icon={ShieldCheckIcon} text="Seguro y sin compromiso" data-id="element-1118" />
              <Badge icon={ZapIcon} text={<>
                    <span className="md:hidden" data-id="element-1120">En minutos</span>
                    <span className="hidden md:inline" data-id="element-1121">
                      Resultados en minutos
                    </span>
                  </>} data-id="element-1119" />
              <Badge icon={CheckCircle2Icon} text={<>
                    <span className="md:hidden" data-id="element-1123">Más chances</span>
                    <span className="hidden md:inline" data-id="element-1124">
                      Más chances y mejor tasa
                    </span>
                  </>} data-id="element-1122" />
            </div>
          </div>

          {/* ILUSTRACIÓN */}
          <div className="hidden lg:flex items-center justify-center relative" data-id="element-1125">
            <div className="relative w-full max-w-2xl h-[600px]" data-id="element-1126">
              <div className="absolute inset-0 bg-gradient-to-r from-[#2B5FD9]/20 to-[#1E2A3A]/20 rounded-full blur-3xl" data-id="element-1127" />

              {/* Card 1 */}
              <div className="absolute top-20 left-0 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 transform -rotate-6 transition-transform duration-300 hover:rotate-0" data-id="element-1128">
                <div className="flex items-center justify-between mb-4" data-id="element-1129">
                  <span className="text-sm font-semibold text-[#0A1628]" data-id="element-1130">
                    Opción con mayor aprobación
                  </span>
                  <span className="px-2 py-1 bg-[#2B5FD9]/10 text-[#2B5FD9] text-xs font-bold rounded-full" data-id="element-1131">
                    Mejor tasa
                  </span>
                </div>
                <div className="space-y-3" data-id="element-1132">
                  <div className="flex justify-between text-sm" data-id="element-1133">
                    <span className="text-[#6B7280]" data-id="element-1134">Probabilidad aprox.:</span>
                    <span className="font-bold text-[#0A1628]" data-id="element-1135">Alta</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1136">
                    <span className="text-[#6B7280]" data-id="element-1137">Monto máximo:</span>
                    <span className="font-bold text-[#0A1628]" data-id="element-1138">UF 3.500</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1139">
                    <span className="text-[#6B7280]" data-id="element-1140">Dividendo estimado:</span>
                    <span className="font-medium text-[#0A1628]" data-id="element-1141">$850.000</span>
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div className="absolute top-32 right-12 w-72 bg-[#1E2A3A] rounded-2xl shadow-2xl p-6 transform rotate-3 transition-transform duration-300 hover:rotate-0 z-10" data-id="element-1142">
                <div className="text-center space-y-4" data-id="element-1143">
                  <div className="flex items-center justify-center" data-id="element-1144">
                    <div className="w-16 h-16 bg-[#2B5FD9]/20 rounded-full flex items-center justify-center" data-id="element-1145">
                      <CheckCircle2Icon className="w-10 h-10 text-[#2B5FD9]" data-id="element-1146" />
                    </div>
                  </div>
                  <h3 className="text-white font-bold text-xl" data-id="element-1147">
                    Varias instituciones, un solo formulario
                  </h3>
                  <p className="text-[#2B5FD9] text-sm" data-id="element-1148">
                    Más chances de aprobación y mejor tasa
                  </p>
                  <div className="bg-white/10 rounded-xl p-3" data-id="element-1149">
                    <div className="flex items-center justify-between text-white text-sm" data-id="element-1150">
                      <span data-id="element-1151">Tiempo ahorrado</span>
                      <span className="font-bold" data-id="element-1152">≈ 3 semanas</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}